import { createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Card } from '@/components/ui/card';
import { BRAND_NAME } from '@/constants/brand';
import { ROUTES } from '@/constants/routes';
import { meQueryOptions } from '@/hooks/data/use-auth';
import { enterIdentity, resetIdentityEverywhere } from '@/lib/identity';
import {
  federatedLandingPath,
  SIGN_IN_INCOMPLETE_NOTICE,
} from '@/lib/social-auth';
import { loadNamespaces } from '@/lib/i18n/i18n';

const callbackSearchSchema = z.object({
  next: z.string().optional(),
});

export const Route = createFileRoute('/auth/callback')({
  loader: () => loadNamespaces('auth'),
  validateSearch: callbackSearchSchema,
  staticData: { title: 'route.auth_callback.title' },
  beforeLoad: async ({ context, search }) => {
    // The backend set this session's cookies; whatever this tab cached belongs to the previous user.
    resetIdentityEverywhere();

    const user = await context.queryClient
      .fetchQuery(meQueryOptions)
      .catch(() => null);
    const target = federatedLandingPath(user, search.next);

    if (!target) {
      // TanStack Router uses thrown non-Error redirects as control flow.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: ROUTES.LOGIN,
        search: { error: SIGN_IN_INCOMPLETE_NOTICE },
        replace: true,
      });
    }

    // Full document load, exactly as a password login ends: nothing in flight outlives it.
    enterIdentity(target);
  },
  component: SigningInPage,
});

function SigningInPage() {
  const { t } = useTranslation('auth');

  return (
    <main className="bg-bg-page flex min-h-screen items-center justify-center p-6">
      <Card className="border-border-1 flex w-full max-w-md flex-col gap-3 p-8 text-center">
        <img
          src="/images/logo.svg"
          alt={BRAND_NAME}
          className="h-9 w-auto self-center"
        />
        <p className="text-fg-3 text-fs-14">{t('auth_callback.signing_in')}</p>
      </Card>
    </main>
  );
}
